import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import Card from 'components/card';
import { toast } from 'react-toastify';
import { MdDelete, MdEdit, MdContentCopy } from 'react-icons/md';
import GoBack from 'components/actions/GoBack';
import { prebuiltTemplates, PrebuiltTemplate } from 'variables/prebuiltTemplates';

interface FormTemplate {
    _id: string;
    name: string;
    templateSlug?: string;
    industry?: string;
    subIndustry?: string;
    regions?: string[];
    minimumPrice?: number;
    borderColor: string;
    borderWidth: string;
    borderRadius: string;
    backgroundColor: string;
    textColor: string;
    labelColor: string;
    buttonBackgroundColor: string;
    buttonTextColor: string;
    inputBackgroundColor: string;
    inputBorderColor: string;
    inputTextColor: string;
    fontFamily?: string;
    fontSize?: string;
    ctaText?: string;
    isDefault: boolean;
    sheetSyncEnabled?: boolean;
    googleSheetId?: string;
    sheetHeaderMapping?: { [key: string]: string };
}

const defaultTemplate: Omit<FormTemplate, '_id'> = {
    name: 'Nouveau Template',
    industry: '',
    subIndustry: '',
    regions: [],
    minimumPrice: undefined,
    borderColor: '#e5e7eb',
    borderWidth: '1px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    textColor: '#1f2937',
    labelColor: '#374151',
    buttonBackgroundColor: '#4f46e5',
    buttonTextColor: '#ffffff',
    inputBackgroundColor: '#ffffff',
    inputBorderColor: '#d1d5db',
    inputTextColor: '#1f2937',
    fontFamily: 'inherit',
    fontSize: '16px',
    ctaText: 'Envoyer ma demande',
    isDefault: false,
};

const FormTemplateManager = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState<FormTemplate[]>([]);
    const [selectedPrebuilt, setSelectedPrebuilt] = useState<string | null>(null);
    const [user, setUser] = useState<any>(null);
    const [loadingSheetId, setLoadingSheetId] = useState<string | null>(null);

    useEffect(() => {
        fetchTemplates();
        fetchUser();
    }, []);

    const fetchUser = async () => {
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            };
            const response = await axios.get(`${CONFIG.apiUrl}/user/me`, config);
            setUser(response.data);
        } catch (error) {
            console.error('Error fetching user:', error);
        }
    };

    const fetchTemplates = async () => {
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            };
            const response = await axios.get(`${CONFIG.apiUrl}/form-template`, config);
            setTemplates(response.data);
        } catch (error) {
            console.error('Error fetching templates:', error);
            toast.error('Erreur lors du chargement des templates');
        }
    };



    const handleDelete = async (templateId: string) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) {
            return;
        }

        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            };
            await axios.delete(`${CONFIG.apiUrl}/form-template/${templateId}`, config);
            toast.success('Template supprimé avec succès');
            fetchTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            toast.error('Erreur lors de la suppression du template');
        }
    };

    const copyTemplateUrl = (templateId: string) => {
        const url = `${window.location.origin}/public/lead-form?templateId=${templateId}`;
        navigator.clipboard.writeText(url);
        toast.success('URL copiée dans le presse-papier');
    };

    const copyCodeSnippet = (templateId: string) => {
        const url = `${window.location.origin}/public/lead-form?templateId=${templateId}`;
        const snippet = `<iframe src="${url}" width="100%" height="600" frameborder="0"></iframe>`;
        navigator.clipboard.writeText(snippet);
        toast.success('Code snippet copié dans le presse-papier');
    };

    const handleSelectPrebuiltTemplate = async (prebuilt: PrebuiltTemplate) => {
        try {
            setSelectedPrebuilt(prebuilt.slug);
            
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            };

            // Create template with slug - backend will return existing if already created
            const response = await axios.post(
                `${CONFIG.apiUrl}/form-template`,
                {
                    name: prebuilt.name,
                    templateSlug: prebuilt.slug,
                    industry: prebuilt.industry,
                    subIndustry: prebuilt.subIndustry,
                    fields: prebuilt.fields,
                    regions: prebuilt.regions,
                    minimumPrice: prebuilt.minimumPrice,
                    borderColor: prebuilt.borderColor,
                    borderWidth: prebuilt.borderWidth,
                    borderRadius: prebuilt.borderRadius,
                    backgroundColor: prebuilt.backgroundColor,
                    textColor: prebuilt.textColor,
                    labelColor: prebuilt.labelColor,
                    buttonBackgroundColor: prebuilt.buttonBackgroundColor,
                    buttonTextColor: prebuilt.buttonTextColor,
                    inputBackgroundColor: prebuilt.inputBackgroundColor,
                    inputBorderColor: prebuilt.inputBorderColor,
                    inputTextColor: prebuilt.inputTextColor,
                    fontFamily: prebuilt.fontFamily,
                    fontSize: prebuilt.fontSize,
                    ctaText: prebuilt.ctaText,
                    isDefault: false,
                },
                config
            );

            console.log('Template created with response:', response.data);
            console.log('Industry:', response.data.industry, 'SubIndustry:', response.data.subIndustry);
            
            toast.success('Template configuré avec succès');
            setSelectedPrebuilt(null);
            fetchTemplates();
            // Navigate to edit page for the newly created template
            navigate(`/admin/form-templates/edit/${response.data._id}`);
        } catch (error) {
            console.error('Error creating prebuilt template:', error);
            toast.error('Erreur lors de la configuration du template');
            setSelectedPrebuilt(null);
        }
    };



    return (
        <div className="h-full w-full rounded-[20px] pb-8">
            <div className="mt-4 mb-4 flex">
                <GoBack />
            </div>

            {/* Header Card */}
            <div className="w-full pb-[10px] pt-[10px] px-[29px] h-fit mb-6">
                <div className="mt-1">
                    <h1 className="text-xl font-bold text-navy-700 dark:text-white">
                        Templates de formulaire
                    </h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Choisissez un template prêt à l'emploi ou créez le vôtre. Une fois configuré, copiez l'URL du formulaire pour l'intégrer facilement à votre landing page.
                    </p>
                </div>
            </div>

            {/* Steps Guide */}
                <div className="mt-4 px-20 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-leadrs text-white flex items-center justify-center text-xl font-bold">
                                1
                            </div>
                            <p className="mt-2 text-sm font-semibold text-navy-700 dark:text-white text-center">
                                Choisir un template
                            </p>
                        </div>
                    </div>
                    <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <div className="flex items-center gap-4">
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-leadrs text-white flex items-center justify-center text-xl font-bold">
                                2
                            </div>
                            <p className="mt-2 text-sm font-semibold text-navy-700 dark:text-white text-center">
                                Copier le code
                            </p>
                        </div>
                    </div>
                    <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <div className="flex items-center gap-4">
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-leadrs text-white flex items-center justify-center text-xl font-bold">
                                3
                            </div>
                            <p className="mt-2 text-sm font-semibold text-navy-700 dark:text-white text-center">
                                Intégrer sur votre site
                            </p>
                        </div>
                    </div>
                </div>

            {/* Info Banner */}
            <Card extra="mt-4 w-full p-4 mb-6 shadow-3xl rounded-[20px] border border-l-4 !border-l-leadrs bg-gradient-to-r from-purple-50 to-white dark:from-navy-800 dark:to-navy-900">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">💡</span>
                        <h3 className="text-sm font-bold text-leadrs dark:text-white">Comment ça marche ?</h3>
                    </div>
                    <p className="text-sm text-leadrs/80 dark:text-gray-400">
                        Sélectionnez un formulaire ci-dessous, personnalisez-le si besoin, puis copiez l'URL générée pour l'intégrer dans votre site (WordPress, Webflow, Wix, ou n'importe quel CMS). Les leads seront automatiquement envoyés dans votre espace Leadrs.
                    </p>
                </div>
            </Card>

            {/* Pre-built Templates Section */}
            <Card extra="p-6 shadow-3xl rounded-[20px] border mb-6">
                <div className="mb-6">
                    <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-2">
                        🎨 Templates prêts à l'emploi
                    </h2>
                    <p className="text-sm text-gray-600">
                        Cliquez sur un template pour le configurer et le personnaliser selon vos besoins
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {prebuiltTemplates
                        .filter((prebuilt) => !templates.some((template) => template.templateSlug === prebuilt.slug))
                        .map((prebuilt) => (
                        <div
                            key={prebuilt.slug}
                            onClick={() => handleSelectPrebuiltTemplate(prebuilt)}
                            className={`relative cursor-pointer rounded-xl border-2 p-5 transition-all duration-200 hover:shadow-lg hover:-translate-y-1 ${
                                selectedPrebuilt === prebuilt.slug
                                    ? 'border-leadrs bg-purple-50 dark:bg-navy-800'
                                    : 'border-gray-200 bg-white hover:border-leadrs dark:bg-navy-900 dark:border-white/10'
                            }`}
                        >
                            {selectedPrebuilt === prebuilt.slug && (
                                <div className="absolute top-3 right-3 w-7 h-7 bg-leadrs text-white rounded-full flex items-center justify-center font-bold text-sm">
                                    ✓
                                </div>
                            )}
                            <div
                                className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl mb-4"
                                style={{
                                    background: `linear-gradient(135deg, ${prebuilt.backgroundColor} 0%, ${prebuilt.inputBackgroundColor} 100%)`,
                                }}
                            >
                                {prebuilt.icon}
                            </div>
                            <h3 className="text-base font-bold text-navy-700 dark:text-white mb-2">
                                {prebuilt.name}
                                {prebuilt.badge && (
                                    <span className="ml-2 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                                        {prebuilt.badge}
                                    </span>
                                )}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                                {prebuilt.description}
                            </p>
                        </div>
                    ))}
                    
                    {/* Custom Template Card */}
                    <div
                        onClick={() => navigate('/admin/form-templates/new')}
                        className="cursor-pointer rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-5 transition-all duration-200 hover:border-leadrs hover:bg-purple-50 dark:bg-navy-900 dark:border-white/10 dark:hover:bg-navy-800"
                    >
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl mb-4 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-navy-700 dark:to-navy-800">
                            ⚙️
                        </div>
                        <h3 className="text-base font-bold text-navy-700 dark:text-white mb-2">
                            Créer mon formulaire
                        </h3>
                        <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            Personnalisez entièrement votre formulaire avec vos propres champs et design
                        </p>
                    </div>
                </div>
            </Card>

            {/* Existing Templates */}
            <Card extra="p-6 shadow-3xl rounded-[20px] border">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                        📋 Mes templates configurés
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map((template) => (
                        <div
                            key={template._id}
                            onClick={() => navigate(`/admin/form-templates/edit/${template._id}`)}
                            className="relative cursor-pointer rounded-lg border border-gray-200 bg-white p-5 transition-all duration-200 hover:shadow-md hover:border-leadrs dark:bg-navy-900 dark:border-white/10"
                        >
                            {/* Delete button - Top Right */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(template._id);
                                }}
                                className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors dark:hover:bg-red-900/20"
                                title="Supprimer le template"
                            >
                                <MdDelete className="h-5 w-5" />
                            </button>

                            {/* Default badge */}
                            {template.isDefault && (
                                <div className="absolute top-3 left-3 px-2 py-0.5 bg-leadrs text-white text-xs font-medium rounded">
                                    Par défaut
                                </div>
                            )}
                            
                            <div
                                className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl mb-3 mt-2"
                                style={{
                                    background: `linear-gradient(135deg, ${template.backgroundColor} 0%, ${template.buttonBackgroundColor} 100%)`,
                                }}
                            >
                                📋
                            </div>
                            
                            <h3 className="text-base font-bold text-navy-700 dark:text-white mb-1">
                                {template.name}
                            </h3>
                            
                            {(template.industry || template.subIndustry) && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    {template.industry}
                                    {template.subIndustry && template.industry && ' - '}
                                    {template.subIndustry}
                                </p>
                            )}
                            
                            {template.regions && template.regions.length > 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-500 mb-2">
                                    📍 {template.regions.slice(0, 2).join(', ')}
                                    {template.regions.length > 2 && ` +${template.regions.length - 2}`}
                                </p>
                            )}
                            
                            {template.minimumPrice && (
                                <p className="text-xs font-semibold text-leadrs mb-3">
                                    Prix min: {template.minimumPrice}€
                                </p>
                            )}
                            
                            {/* URL Copy - Flat Design */}
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/5">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copyTemplateUrl(template._id);
                                    }}
                                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700"
                                    title="Copier l'URL du formulaire"
                                >
                                    <MdContentCopy className="h-4 w-4" />
                                    Copier l'URL
                                </button>
                                
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copyCodeSnippet(template._id);
                                    }}
                                    className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700"
                                    title="Copier le code d'intégration"
                                >
                                    <MdContentCopy className="h-4 w-4" />
                                    Copier l'Iframe
                                </button>
                                
                                {user?.canSellWithGSheet && (
                                    <button
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            setLoadingSheetId(template._id);
                                            try {
                                                const config = {
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                        Authorization: 'Bearer ' + localStorage.getItem('token'),
                                                    },
                                                };
                                                const response = await axios.post(
                                                    `${CONFIG.apiUrl}/form-template/${template._id}/get-or-create-sheet`,
                                                    {},
                                                    config
                                                );
                                                if (response.data.sheetUrl) {
                                                    window.open(response.data.sheetUrl, '_blank');
                                                    toast.success(response.data.status === 'Sheet created successfully' 
                                                        ? 'Google Sheet créé avec succès!' 
                                                        : 'Google Sheet ouvert');
                                                    // Refresh templates to get updated data
                                                    if (response.data.status === 'Sheet created successfully') {
                                                        fetchTemplates();
                                                    }
                                                }
                                            } catch (error: any) {
                                                console.error('Error getting/creating sheet:', error);
                                                toast.error(error.response?.data?.message || 'Erreur lors de l\'accès à Google Sheet');
                                            } finally {
                                                setLoadingSheetId(null);
                                            }
                                        }}
                                        disabled={loadingSheetId === template._id}
                                        className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Vendre via Google Sheet"
                                    >
                                        {loadingSheetId === template._id ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Chargement...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 12h2v5H7zm4-3h2v8h-2zm4-3h2v11h-2z"/>
                                                </svg>
                                                Vendre via Google Sheet
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {templates.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <p className="text-gray-500 text-lg">Aucun template créé pour le moment</p>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default FormTemplateManager;
